import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from '@material-ui/core'
import { useExameNecroscopicoService } from 'hooks/useExameNecroscopicoService'
import { v4 } from 'uuid'

export const useMapaCorpo = (props) => {
  const { lesoes, setLesoes, paraCicatriz, setSVGElements, odonto } = props
  const theme = useTheme()
  const isDark = theme.palette.type === 'dark'
  const exameNecroscopicoService = useExameNecroscopicoService()

  const [listaRegioesAtivas, setListaRegioesAtivas] = useState([])
  const [renderSelectOption, setRenderSelectOption] = useState(false)

  const options = useSelector((state) => state.options)
  const listaRegioesMouseOver = useRef([])
  const mousePos = useRef({ x: 0, y: 0 })
  const dadosNovaLesao = useRef({})
  const lesoesCurrent = useRef([])

  const positionYAtualFront = useRef(40)
  const positionYAtualBack = useRef(40)
  const positionYAtualLeft = useRef(40)
  const positionYAtualRight = useRef(40)

  // Cores dinâmicas para Dark Mode
  const colors = {
    strokeColor: isDark ? 'rgba(255, 255, 255, 0.85)' : '#ffffff',
    fillColor: isDark ? '#3a3f44' : '#989FA7',
    hoverColor: isDark ? '#50565c' : '#848A91',
    selectedColor: isDark ? '#1a1c1e' : '#383837',
    legendColor: isDark ? '#ffffff' : '#000000',
    lineColor: isDark ? 'rgba(255, 255, 255, 0.7)' : '#000000',
    defaultCircleColor: isDark ? '#ffffff' : '#282C33'
  }

  // Estado local para o formulário da nova lesão substituindo useFormState legado
  const [formValues, setFormValues] = useState({
    tipo: null,
    descricao: '',
    tipoTextoLivre: '',
    observacoes: '',
    pafHaloEnxugo: false,
    pafAureolaEquimotica: false,
    pafOrlaEscoriacao: false,
    pafZonaTatuagem: false,
    pafZonaEsfumacamento: false,
    pafZonaQueimadura: false,
    pafAspectoEstrelado: false,
    pafMarcaCano: false,
    pafTipoBorda: 'NÃO DEFINIDO',
    incisaoRegiaoMaisAprofundada: 'NÃO DEFINIDO',
    incisaoRegiaoCaudaEscoriacao: 'NÃO DEFINIDO',
    incisaoBorda: 'NÃO DEFINIDO',
    perfuroIncisaForma: 'NÃO DEFINIDO',
    metalizacao: false,
    eritema: false,
    flictema: false,
    carbonizacao: false,
    necrose: false,
    tipoQueimaduraEletrica: 'NÃO DEFINIDO',
    tipoCogumeloEspuma: 'NÃO DEFINIDO',
    sulcoOrientacao: 'NÃO DEFINIDO',
    sulcoMultiplicidade: 'NÃO DEFINIDO',
    sulcoContinuidade: 'NÃO DEFINIDO',
    sulcoTonalidade: 'NÃO DEFINIDO',
    sulcoProfundidade: 'NÃO DEFINIDO',
    sulcoTumefeito: false,
    sulcoMargensElevadas: false,
    sulcoLeitoDeprimido: false,
    sulcoPeleEnrugada: false,
  })

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleChangeNewValueByNameProperty = (event, newValue, name) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: newValue,
    }))
  }

  const resetFormValues = () => {
    setFormValues({
      tipo: null,
      descricao: '',
      tipoTextoLivre: '',
      observacoes: '',
      pafHaloEnxugo: false,
      pafAureolaEquimotica: false,
      pafOrlaEscoriacao: false,
      pafZonaTatuagem: false,
      pafZonaEsfumacamento: false,
      pafZonaQueimadura: false,
      pafAspectoEstrelado: false,
      pafMarcaCano: false,
      pafTipoBorda: 'NÃO DEFINIDO',
      incisaoRegiaoMaisAprofundada: 'NÃO DEFINIDO',
      incisaoRegiaoCaudaEscoriacao: 'NÃO DEFINIDO',
      incisaoBorda: 'NÃO DEFINIDO',
      perfuroIncisaForma: 'NÃO DEFINIDO',
      metalizacao: false,
      eritema: false,
      flictema: false,
      carbonizacao: false,
      necrose: false,
      tipoQueimaduraEletrica: 'NÃO DEFINIDO',
      tipoCogumeloEspuma: 'NÃO DEFINIDO',
      sulcoOrientacao: 'NÃO DEFINIDO',
      sulcoMultiplicidade: 'NÃO DEFINIDO',
      sulcoContinuidade: 'NÃO DEFINIDO',
      sulcoTonalidade: 'NÃO DEFINIDO',
      sulcoProfundidade: 'NÃO DEFINIDO',
      sulcoTumefeito: false,
      sulcoMargensElevadas: false,
      sulcoLeitoDeprimido: false,
      sulcoPeleEnrugada: false,
    })
  }

  const formNovaLesao = {
    formState: {
      values: formValues,
      errors: {},
      touched: {},
    },
    handleChange,
    handleChangeNewValueByNameProperty,
    resetFormValues,
  }

  useEffect(() => {
    if (!options.tiposLesoes && !paraCicatriz) {
      exameNecroscopicoService.listarTiposLesoesGlobal()
    }
  }, [])

  const isRegiaoSelecionada = (id) => {
    return listaRegioesAtivas.includes(id)
  }

  const getRotuloLesao = (tipo) => {
    let rotulo = '(L)'
    switch (tipo?.cod) {
      case 'PAF_ENTRADA':
        rotulo = '(E_PAF)'
        break
      case 'PAF_SAIDA':
        rotulo = '(S_PAF)'
        break
      case 'PAF':
        rotulo = '(PAF)'
        break
      case 'PAF_TANGENCIAL':
        rotulo = '(TAN_PAF)'
        break
      case 'TIRO_ENCOSTADO':
        rotulo = '(ENC_PAF)'
        break
      default:
        break
    }
    return rotulo
  }

  const getIndiceLesoesNoMapa = (mapa) => {
    let indice = 0
    lesoesCurrent.current.forEach((lesaoCurrent) => {
      if (lesaoCurrent.mapa === mapa) {
        indice += 1
      }
    })
    return indice
  }

  const addLesaoMapa = (pointX, pointY, idFront, mapa, tipo, indiceParameter) => {
    const svg = document.getElementById(mapa)
    if (!svg) return

    const index = indiceParameter || lesoesCurrent.current.length
    const indice = `0${index}`.replace(/([0])([0-9][0-9])/, '$2')

    const xmlns = 'http://www.w3.org/2000/svg'
    const newGroup = document.createElementNS(xmlns, 'g')
    newGroup.setAttribute('id', idFront)
    newGroup.setAttribute('class', 'groupLesao')

    const newCircle = document.createElementNS(xmlns, 'circle')
    newCircle.addEventListener('click', (e) => removerLesao(e, idFront))
    newCircle.setAttribute('cursor', 'pointer')
    newCircle.setAttribute('cx', pointX)
    newCircle.setAttribute('cy', pointY)
    newCircle.setAttribute('r', '7')

    let color = colors.defaultCircleColor
    if (tipo?.cod === 'PAF_ENTRADA') {
      color = '#00FF00'
    } else if (tipo?.cod === 'PAF_SAIDA') {
      color = '#FF0000'
    } else if (tipo?.cod === 'PAF') {
      color = '#0000FF'
    }
    newCircle.setAttribute('fill', color)

    let positionLegendY = null
    if (mapa === 'svg-front') {
      positionLegendY = positionYAtualFront.current
      positionYAtualFront.current += 40
    } else if (mapa === 'svg-back') {
      positionLegendY = positionYAtualBack.current
      positionYAtualBack.current += 40
    } else if (mapa === 'svg-left') {
      positionLegendY = positionYAtualLeft.current
      positionYAtualLeft.current += 40
    } else if (mapa === 'svg-right') {
      positionLegendY = positionYAtualRight.current
      positionYAtualRight.current += 40
    }

    let positionLegendX = 10
    let positionLineLengendX = 210
    let positionLineLegendY = positionLegendY - 2

    const indiceNoMapa = getIndiceLesoesNoMapa(mapa)
    if (indiceNoMapa >= 26) {
      positionLegendY = ((indiceNoMapa % 26) + 1) * 40
      positionLegendX = 1100
      positionLineLengendX = 1100
      positionLineLegendY = positionLegendY - 4
    }

    const newTextLegend = document.createElementNS(xmlns, 'text')
    newTextLegend.setAttribute('x', positionLegendX)
    newTextLegend.setAttribute('y', positionLegendY)
    newTextLegend.setAttribute('font-size', '20')
    newTextLegend.setAttribute('font-weight', 'bold')
    newTextLegend.setAttribute('fill', colors.legendColor)
    newTextLegend.setAttribute('font-family', 'Arial')

    const label = paraCicatriz ? tipo : 'Lesão'
    const rotulo = !paraCicatriz ? getRotuloLesao(tipo) : ''
    const novaLegenda = document.createTextNode(`${indice} - ${label} ${rotulo}`)
    newTextLegend.appendChild(novaLegenda)

    const newLineLegend = document.createElementNS(xmlns, 'line')
    newLineLegend.setAttribute('x1', pointX)
    newLineLegend.setAttribute('y1', pointY)
    newLineLegend.setAttribute('x2', pointX)
    newLineLegend.setAttribute('y2', positionLineLegendY)
    newLineLegend.setAttribute('stroke', colors.lineColor)

    const newLineLegend2 = document.createElementNS(xmlns, 'line')
    newLineLegend2.setAttribute('x1', pointX)
    newLineLegend2.setAttribute('y1', positionLineLegendY)
    newLineLegend2.setAttribute('x2', positionLineLengendX)
    newLineLegend2.setAttribute('y2', positionLineLegendY)
    newLineLegend2.setAttribute('stroke', colors.lineColor)

    newGroup.appendChild(newTextLegend)
    newGroup.appendChild(newLineLegend)
    newGroup.appendChild(newLineLegend2)
    newGroup.appendChild(newCircle)
    svg.appendChild(newGroup)

    atualizarSVGElements()
  }

  const defaultColors = {
    strokeColor: '#ffffff',
    fillColor: '#989FA7',
    selectedColor: '#383837',
    legendColor: '#000000',
    lineColor: '#000000',
    defaultCircleColor: '#282C33',
  }

  const prepararSVGParaExportacao = (svgElement) => {
    if (!svgElement) return ''
    const clone = svgElement.cloneNode(true)

    const paths = clone.querySelectorAll('path')
    paths.forEach((path) => {
      if (path.id) {
        path.setAttribute('stroke', defaultColors.strokeColor)
        path.setAttribute('stroke-width', '3.5')
        if (isRegiaoSelecionada(path.id)) {
          path.setAttribute('fill', defaultColors.selectedColor)
        } else {
          path.setAttribute('fill', defaultColors.fillColor)
        }
      }
    })

    const texts = clone.querySelectorAll('text')
    texts.forEach((text) => {
      text.setAttribute('fill', defaultColors.legendColor)
    })

    const lines = clone.querySelectorAll('line')
    lines.forEach((line) => {
      line.setAttribute('stroke', defaultColors.lineColor)
    })

    const circles = clone.querySelectorAll('circle')
    circles.forEach((circle) => {
      const currentFill = circle.getAttribute('fill')
      if (currentFill === '#ffffff') {
        circle.setAttribute('fill', defaultColors.defaultCircleColor)
      }
    })

    return clone.outerHTML
  }

  const atualizarSVGElements = () => {
    const svgFront = document.getElementById('svg-front')
    const svgBack = document.getElementById('svg-back')
    const svgLeft = document.getElementById('svg-left')
    const svgRight = document.getElementById('svg-right')

    if (setSVGElements && svgFront && svgBack && svgLeft && svgRight) {
      setSVGElements({
        svgFront: prepararSVGParaExportacao(svgFront),
        svgRight: prepararSVGParaExportacao(svgRight),
        svgLeft: prepararSVGParaExportacao(svgLeft),
        svgBack: prepararSVGParaExportacao(svgBack),
      })
    }
  }


  useEffect(() => {
    atualizarSVGElements()
  }, [])


  const removerLesao = (event, idFront) => {
    const novaListaLesoes = []
    lesoesCurrent.current.forEach((lesaoRemovida) => {
      const element = document.getElementById(lesaoRemovida.idFront)
      if (lesaoRemovida.idFront !== idFront) {
        novaListaLesoes.push(lesaoRemovida)
      }
      if (element) {
        element.remove()
      }
    })

    lesoesCurrent.current = novaListaLesoes

    positionYAtualBack.current = 40
    positionYAtualFront.current = 40
    positionYAtualLeft.current = 40
    positionYAtualRight.current = 40

    lesoesCurrent.current.forEach((lesaoCurrent, index) => {
      addLesaoMapa(
        lesaoCurrent.px,
        lesaoCurrent.py,
        lesaoCurrent.idFront,
        lesaoCurrent.mapa,
        lesaoCurrent.tipo,
        index + 1
      )
    })

    setLesoes(lesoesCurrent.current.map((l) => l))
    atualizarSVGElements()
  }

  const redesenharLesoes = () => {
    lesoesCurrent.current.forEach((lesao) => {
      const element = document.getElementById(lesao.idFront)
      if (element) {
        element.remove()
      }
    })

    positionYAtualBack.current = 40
    positionYAtualFront.current = 40
    positionYAtualLeft.current = 40
    positionYAtualRight.current = 40

    lesoesCurrent.current.forEach((lesaoCurrent, index) => {
      addLesaoMapa(
        lesaoCurrent.px,
        lesaoCurrent.py,
        lesaoCurrent.idFront,
        lesaoCurrent.mapa,
        lesaoCurrent.tipo,
        index + 1
      )
    })
  }

  const handleSelectLesao = () => {
    const novoId = v4()
    const novaLesaoItem = {
      idFront: novoId,
      px: dadosNovaLesao.current.px,
      py: dadosNovaLesao.current.py,
      mapa: dadosNovaLesao.current.mapa,
      regioes: [dadosNovaLesao.current.regioes],
      observacoes: formNovaLesao.formState.values.observacoes,
      tipo: formNovaLesao.formState.values.tipo,
      tipoTextoLivre: formNovaLesao.formState.values.tipoTextoLivre,
    }

    lesoesCurrent.current.push(novaLesaoItem)
    setLesoes([
      ...lesoes,
      {
        ...novaLesaoItem,
        ...formNovaLesao.formState.values,
      },
    ])

    addLesaoMapa(
      dadosNovaLesao.current.px,
      dadosNovaLesao.current.py,
      novoId,
      dadosNovaLesao.current.mapa,
      formNovaLesao.formState.values.tipo,
      false
    )

    formNovaLesao.resetFormValues()
    setRenderSelectOption(false)
  }

  const handleClickSvg = (event, svgId) => {
    mousePos.current = { x: event.clientX, y: event.clientY }
    setRenderSelectOption(true)
    const svg = document.getElementById(svgId)
    if (!svg) return

    const pt = svg.createSVGPoint()

    function cursorPoint(evt) {
      pt.x = evt.clientX
      pt.y = evt.clientY
      return pt.matrixTransform(svg.getScreenCTM().inverse())
    }
    const pointsRelative = cursorPoint(event)

    dadosNovaLesao.current = {
      ...dadosNovaLesao.current,
      px: pointsRelative.x,
      py: pointsRelative.y,
      mapa: svgId,
    }
  }

  const handleClickRegiao = (event) => {
    const regiaoSelecionada = event.target.id
    dadosNovaLesao.current = {
      ...dadosNovaLesao.current,
      regioes: regiaoSelecionada,
    }
  }

  const handleMouseOver = (event) => {
    const mapa = document.getElementById('div_mapa_corpo')
    if (!mapa) return

    let isRegiaoAtiva = false
    listaRegioesMouseOver.current.forEach((regiao) => {
      if (regiao === event.target.id) {
        isRegiaoAtiva = true
      }
    })

    if (!isRegiaoAtiva) {
      const element = mapa.querySelector(`#${event.target.id}`)
      if (element) {
        element.setAttribute('fill', colors.hoverColor)
      }
    }
  }

  const handleMouseOut = (event) => {
    const mapa = document.getElementById('div_mapa_corpo')
    if (!mapa) return

    let isRegiaoAtiva = false
    listaRegioesMouseOver.current.forEach((regiao) => {
      if (regiao === event.target.id) {
        isRegiaoAtiva = true
      }
    })

    if (!isRegiaoAtiva) {
      const element = mapa.querySelector(`#${event.target.id}`)
      if (element) {
        element.setAttribute('fill', colors.fillColor)
      }
    }
  }

  // Sincronização inicial de lesões recebidas por prop
  useEffect(() => {
    lesoesCurrent.current = lesoes.map((lesao) => lesao)
    redesenharLesoes()
  }, [])

  // Efeito reativo para aplicar estilos e listeners nos paths SVG e adaptar ao Dark Mode
  useEffect(() => {
    const svg = document.getElementById('div_mapa_corpo')
    if (!svg) return

    const elements = svg.querySelectorAll('path')
    elements.forEach((element) => {
      if (element.id) {
        element.addEventListener('click', handleClickRegiao)
        element.addEventListener('mouseover', handleMouseOver)
        element.addEventListener('mouseout', handleMouseOut)

        element.setAttribute('stroke', colors.strokeColor)
        element.setAttribute('stroke-width', '3.5')
        if (isRegiaoSelecionada(element.id)) {
          element.setAttribute('fill', colors.selectedColor)
        } else {
          element.setAttribute('fill', colors.fillColor)
        }
      }
    })

    redesenharLesoes()
    atualizarSVGElements()

    return () => {
      elements.forEach((element) => {
        if (element.id) {
          element.removeEventListener('click', handleClickRegiao)
          element.removeEventListener('mouseover', handleMouseOver)
          element.removeEventListener('mouseout', handleMouseOut)
        }
      })
    }
  }, [isDark, listaRegioesAtivas])

  // Limpa formulário quando fechar popover
  useEffect(() => {
    if (!renderSelectOption) {
      resetFormValues()
    }
  }, [renderSelectOption])

  const handleClosePopoverLesao = () => {
    setRenderSelectOption(false)
  }

  return {
    listaRegioesAtivas,
    renderSelectOption,
    formNovaLesao,
    options,
    mousePos,
    isDark,
    colors,
    handleClosePopoverLesao,
    handleSelectLesao,
    handleClickSvg,
  }
}
